function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useState, useCallback, useEffect, memo } from 'react';
import { COOKIE_BANNER_ANIMATION_DURATION_IN_SECONDS } from '../constants.js';
import { useCookie } from '../CookieContext.js';
import defaultTheme from '../defaultTheme.js';
import useIsVisibleWithTransition from '../hooks/useIsVisibleWithTransition.js';
import useSavedTrackingPreference, { useSavedTrackingPreferenceFromMobileApp } from '../hooks/useSavedTrackingPreference.js';
import useSetTrackingPreference from '../hooks/useSetTrackingPreference.js';
import { useTrackingManager } from '../TrackingManagerContext.js';
import getDefaultTrackingPreference from '../utils/getDefaultTrackingPreference.js';
import { getDomainWithoutSubdomain } from '../utils/getDomain.js';
import isOptOut from '../utils/isOptOut.js';
import { persistMobileAppPreferences } from '../utils/persistMobileAppPreferences.js';
import BannerContent from './CookieBannerContent.js';
import BannerContentSlimExp from './CookieBannerContentSlimExp.js';
import CookiePreferencesModal from './CookiePreferencesModal.js';
import UIProviders from './UIProviders.js';
import { jsxs, jsx } from 'react/jsx-runtime';
const PREFERENCE_EXPIRATION_YEAR = 1;
const expiration = PREFERENCE_EXPIRATION_YEAR * 365;
const cookieOptions = {
  expires: expiration,
  domain: getDomainWithoutSubdomain(),
  path: '/'
};
const useBanner = () => {
  const savedPreference = useSavedTrackingPreference();
  const mobileAppPreference = useSavedTrackingPreferenceFromMobileApp();
  const preference = savedPreference || mobileAppPreference;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isModalOpen = _useState2[0],
    setIsModalOpen = _useState2[1];
  const _useState3 = useState(!!preference),
    _useState4 = _slicedToArray(_useState3, 2),
    hasDismissed = _useState4[0],
    setHasDismissed = _useState4[1];
  const _useTrackingManager = useTrackingManager(),
    region = _useTrackingManager.region,
    config = _useTrackingManager.config;
  const optOutRegion = isOptOut(region);
  const defaultPreferences = getDefaultTrackingPreference(region, config);
  const setTrackingPreference = useSetTrackingPreference();
  const _useCookie = useCookie('advertising_sharing_allowed'),
    _useCookie2 = _slicedToArray(_useCookie, 2),
    setAdvertisingSharingAllowedCookie = _useCookie2[1];
  const handleBannerDismiss = useCallback(() => {
    // Only set default tracking preferences on dismiss if we are in an opt out
    // country and if a preference doesn't already exist
    if (optOutRegion && !preference) {
      setTrackingPreference(defaultPreferences);
    }
    setHasDismissed(true);
  }, [defaultPreferences, optOutRegion, setTrackingPreference, preference]);
  const handleAcceptAll = useCallback(() => {
    // The default preference for the DEFAULT region includes all consent
    const preferences = getDefaultTrackingPreference('DEFAULT', config);
    setTrackingPreference({
      region,
      consent: preferences.consent
    });
    setHasDismissed(true);
  }, [config, setTrackingPreference, region]);
  const _useIsVisibleWithTran = useIsVisibleWithTransition(!hasDismissed, COOKIE_BANNER_ANIMATION_DURATION_IN_SECONDS * 1000),
    _useIsVisibleWithTran2 = _slicedToArray(_useIsVisibleWithTran, 2),
    isBannerVisible = _useIsVisibleWithTran2[0],
    closeBannerWithTransition = _useIsVisibleWithTran2[1];
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    closeBannerWithTransition(() => setHasDismissed(true));
  }, [setIsModalOpen, setHasDismissed, closeBannerWithTransition]);
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);
  const shouldSaveMobileAppPreferences = Boolean(!savedPreference && mobileAppPreference);
  useEffect(() => {
    if (shouldSaveMobileAppPreferences && mobileAppPreference) {
      // Set the is_mobile_app cookie.
      persistMobileAppPreferences();
      // advertising sharing disabled, if `is_mobile_app` is set
      const isEnabled = false;
      const updatedAt = Date.now();
      setAdvertisingSharingAllowedCookie({
        value: isEnabled,
        updatedAt
      }, cookieOptions);
      // Set the cookie banner preferences.
      setTrackingPreference(mobileAppPreference);
    }
  }, [shouldSaveMobileAppPreferences]);
  return {
    hasDismissed,
    handleBannerDismiss,
    handleAcceptAll,
    closeModal,
    isModalOpen,
    openModal,
    isBannerVisible,
    closeBannerWithTransition
  };
};
function Banner({
  theme,
  isExperimentalBanner,
  sidebarWidth,
  onBannerPreviouslyDismissed,
  onBannerWillEnter,
  onBannerWillExit,
  bannerRef
}) {
  const _useBanner = useBanner(),
    hasDismissed = _useBanner.hasDismissed,
    handleBannerDismiss = _useBanner.handleBannerDismiss,
    handleAcceptAll = _useBanner.handleAcceptAll,
    closeModal = _useBanner.closeModal,
    openModal = _useBanner.openModal,
    isModalOpen = _useBanner.isModalOpen,
    isBannerVisible = _useBanner.isBannerVisible,
    closeBannerWithTransition = _useBanner.closeBannerWithTransition;
  useEffect(() => {
    if (hasDismissed) {
      onBannerPreviouslyDismissed?.();
    }
  }, []);
  return /*#__PURE__*/jsxs(UIProviders, {
    theme: theme || defaultTheme,
    children: [/*#__PURE__*/jsx(CookiePreferencesModal, {
      onClose: closeModal,
      isOpen: isModalOpen
    }), isExperimentalBanner ? /*#__PURE__*/jsx(BannerContentSlimExp, {
      onDismiss: handleBannerDismiss,
      showPreferencesModal: openModal,
      hasDismissed: hasDismissed
    }) : /*#__PURE__*/jsx(BannerContent, {
      isVisible: isBannerVisible,
      closeWithTransition: closeBannerWithTransition,
      onDismiss: handleBannerDismiss,
      onAccept: handleAcceptAll,
      showPreferencesModal: openModal,
      hasDismissed: hasDismissed,
      sidebarWidth: sidebarWidth,
      onBannerWillEnter: onBannerWillEnter,
      onBannerWillExit: onBannerWillExit,
      bannerRef: bannerRef
    })]
  });
}
const CookieBanner = /*#__PURE__*/memo(Banner);
export { CookieBanner as default, useBanner };
//# sourceMappingURL=CookieBanner.js.map
